if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.dataset.inventoryManagement
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];

        if (!inventory) {
          this.hidden = true;
          return;
        }

        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.inventory_management
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {string} inventoryManagement - inventory tracking service, or empty when disabled
     */
    updateInventory(count, available, inventoryPolicy, inventoryManagement) {
      if (!inventoryManagement) {
        this.hidden = true;
        return;
      }

      const veryLow = parseInt(this.dataset.thresholdVeryLow, 10) || 1;
      const low = parseInt(this.dataset.thresholdLow, 10) || 9;
      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= veryLow) {
        inventoryLevel = 'very_low';
      } else if (count <= low) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const isGranularInStock = count > 0;
      const showOutOfStock = (this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false');

      if (isGranularInStock || showOutOfStock) {
        this.hidden = false;
        this.setAttribute('data-inventory-level', inventoryLevel);

        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (count === 1) {
          this.inventoryNotice.innerText = theme.strings.lastOne || 'Last one';
        } else if (count >= 2 && count <= 4) {
          this.inventoryNotice.innerText = (theme.strings.xLeft || '[quantity] left').replace('[quantity]', count);
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStockUnder10 || 'Low stock (under 10)';
        } else if (inventoryLevel === 'normal') {
          this.inventoryNotice.innerText = theme.strings.inStock10Plus || 'In stock (10+)';
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.lastOne || 'Last one';
        } else if (inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          const scale = parseInt(this.dataset.scale, 10) || (low + 1);
          let newWidth;
          if (inventoryLevel === 'in_stock') {
            newWidth = 100;
          } else if (count >= scale || inventoryLevel === 'normal') {
            newWidth = 100;
          } else {
            newWidth = ((100 / scale) * Math.max(count, 0)).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
